<script>
import {
  QUICK_EMOJIS,
  ICONS,
  TASK_FROM_MESSAGE,
  MEDIA_TYPES,
  SEND_TO_DRIVE,
} from '@modules/atendimento/constants/Text'
import { FEATURES } from '@constants/Text'
import AawzDriveFolderPickerMenu from '@modules/aawz-drive/components/aawz-drive-folder-picker-menu/aawz-drive-folder-picker-menu.vue'

export default {
  name: 'ChatMessageActions',
  components: {
    AawzDriveFolderPickerMenu,
  },
  props: {
    mensagem: {
      type: Object,
      required: true,
    },
    bubbleColor: {
      type: String,
      default: '#ffffff',
    },
    // Feature flags opt-in pra ESCONDER (boolean default false por convenção).
    // Atendimento não passa → mostra "Nota interna" e "Criar tarefa".
    // Solicitacoes (V1) passa hide-note + hide-task → não oferece esses itens.
    hideNote: {
      type: Boolean,
      default: false,
    },
    hideTask: {
      type: Boolean,
      default: false,
    },
    // Só habilita "Enviar para AAWZ Drive" quando a conversa tem cliente
    // vinculado — sem cliente não há Drive de destino. Repassado pela cadeia
    // chat-window → chat-window-messages → chat-message.
    hasLinkedClient: {
      type: Boolean,
      default: false,
    },
    // Cliente vinculado à conversa — usado como rootKey para ler a árvore do
    // Drive já carregada no store e como destino do upload. Repassado pela
    // cadeia chat-window → chat-window-messages → chat-message.
    idClienteApp: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      icons: ICONS,
      showMenu: false,
      quickEmojis: QUICK_EMOJIS,
      // Controle manual dos dois v-menu de Drive (não open-on-hover): o
      // conteúdo é teletransportado pelo Vuetify pra fora da árvore DOM do
      // menu, então o containment check interno de mouseleave nunca
      // reconhece o hover num nível descendente e fecha cedo demais — quem
      // fecha errado é justamente ESTE nível raiz enquanto o usuário
      // interage 2+ níveis abaixo em AawzDriveFolderPickerMenu. O evento
      // `@active` (ver aawz-drive-folder-picker-menu.vue) borbulha pela
      // árvore de componentes a partir do nível mais profundo hovered e
      // cancela cancelDriveClose/cancelDriveRenameClose aqui, independente
      // de onde o Vuetify posicionou cada nível no DOM.
      driveMenuOpen: false,
      driveMenuCloseTimer: null,
      driveRenameMenuOpen: false,
      driveRenameMenuCloseTimer: null,
    }
  },
  computed: {
    isNote() {
      return !!this.mensagem.is_note
    },
    isOutgoing() {
      return this.mensagem.direcao === 'saida'
    },
    canReply() {
      return !this.isNote
    },
    canEdit() {
      // Só notas internas podem ser editadas — o backend só expõe edição
      // pra `is_note: true`. Mensagens reais do WhatsApp não suportam edição.
      return this.isNote
    },
    canDelete() {
      return this.isNote
    },
    canReact() {
      return !this.isNote && !!this.mensagem.whatsapp_message_id
    },
    // Mensagens otimistas ainda não têm id real no banco — não dá para
    // vincular uma tarefa (EC-8). Escondemos "Criar tarefa" até confirmar.
    isOptimistic() {
      const id = this.mensagem && this.mensagem.id
      return typeof id === 'string' && id.indexOf('optimistic-') === 0
    },
    // Gated por FEATURES.TASK_V2 (Gerenciador de tarefas) — sem a flag, o
    // item não é renderizado. $access é global (não mockar em testes).
    canCreateTask() {
      return this.$access[FEATURES.TASK_V2] === true && !this.isOptimistic
    },
    // "Enviar para AAWZ Drive" só aparece para mensagens com mídia, em
    // conversa com cliente vinculado, e nunca para notas internas ou
    // mensagens otimistas (ainda sem id real no banco).
    canSendToDrive() {
      return (
        this.hasLinkedClient &&
        MEDIA_TYPES.includes(this.mensagem.tipo) &&
        !this.isNote &&
        !this.isOptimistic
      )
    },
    // Nó raiz da árvore do Drive do cliente (já carregada no store pelo
    // pré-fetch do chat-window). Serve tanto como destino "raiz" quanto como
    // fonte das pastas de primeiro nível do menu em cascata.
    driveRootNode() {
      const getters = this.$store && this.$store.getters
      const getter = getters && getters['aawz-drive/aawzDrive']
      return typeof getter === 'function' ? getter(this.idClienteApp) : null
    },
    // Falso enquanto o pré-fetch (chat-window) ainda não populou a árvore —
    // desabilita o item "raiz" pra não emitir folder:null antes da hora.
    driveTreeLoaded() {
      return !!this.driveRootNode
    },
    // Pastas de primeiro nível (filhos-pasta da raiz) — alimenta o menu
    // recursivo AawzDriveFolderPickerMenu.
    driveRootFolders() {
      const root = this.driveRootNode
      const children = (root && root.children) || []
      return children.filter(
        (node) => node && (node.is_folder || node.type === 'folder')
      )
    },
    sendToDriveLabel() {
      return SEND_TO_DRIVE.MENU_LABEL
    },
    sendToDriveRenameLabel() {
      return SEND_TO_DRIVE.MENU_LABEL_RENAME
    },
    driveIcon() {
      return SEND_TO_DRIVE.MENU_ICON
    },
    menuItems() {
      const items = []
      if (this.canReply) {
        items.push({
          key: 'reply',
          label: 'Responder',
          icon: this.icons.reply,
          action: this.onReply,
        })
      }
      if (this.canEdit) {
        items.push({
          key: 'edit',
          label: 'Editar',
          icon: this.icons.edit,
          action: this.onEdit,
        })
      }
      if (!this.hideNote && !this.isNote) {
        items.push({
          key: 'note',
          label: 'Nota interna',
          icon: 'mdi-note-plus-outline',
          action: this.onNote,
        })
      }
      if (!this.hideTask && this.canCreateTask) {
        items.push({
          key: 'task',
          label: TASK_FROM_MESSAGE.MENU_LABEL,
          icon: TASK_FROM_MESSAGE.MENU_ICON,
          action: this.onCreateTask,
        })
      }
      // "Enviar para AAWZ Drive" (+ variante "e renomear") e "Remover nota"
      // não entram aqui: os dois de Drive precisam do submenu em cascata e o
      // delete precisa ficar por último — ambos são markup dedicado no
      // template, após este v-for genérico.
      return items
    },
  },
  beforeDestroy() {
    this.cancelDriveClose()
    this.cancelDriveRenameClose()
  },
  methods: {
    onReply() {
      this.$emit('reply', this.mensagem)
    },
    onEdit() {
      this.$emit('edit', this.mensagem)
    },
    onNote() {
      this.$emit('note', this.mensagem)
    },
    onCreateTask() {
      this.$emit('create-task', this.mensagem)
    },
    // Pasta escolhida no menu em cascata — fecha o menu e emite o envio direto.
    // Guard de folder: o item "raiz" fica desabilitado até driveTreeLoaded,
    // mas mantemos o early-return aqui também (defesa em profundidade).
    onPickDriveFolder(folder) {
      if (!folder) return
      this.closeAllMenus()
      this.$emit('send-to-drive', { mensagem: this.mensagem, folder })
    },
    // Igual ao anterior, mas o pai abre o modal de renomear antes de enviar.
    onPickDriveFolderRename(folder) {
      if (!folder) return
      this.closeAllMenus()
      this.$emit('send-to-drive-rename', { mensagem: this.mensagem, folder })
    },
    onDelete() {
      this.$emit('delete', this.mensagem)
    },
    onSelectEmoji(emoji) {
      this.showMenu = false
      this.$emit('react', { mensagem: this.mensagem, emoji })
    },
    onMenuToggle(val) {
      this.showMenu = val
      if (!val) this.resetDriveMenus()
      this.$emit('picker-toggle', val)
    },
    // Fecha tudo (menu principal + os dois submenus de Drive) e reseta o
    // estado deles — sem isso, reabrir o menu principal poderia reaproveitar
    // um driveMenuOpen=true "fantasma" de uma seleção anterior.
    closeAllMenus() {
      this.showMenu = false
      this.resetDriveMenus()
      this.$emit('picker-toggle', false)
    },
    resetDriveMenus() {
      this.cancelDriveClose()
      this.driveMenuOpen = false
      this.cancelDriveRenameClose()
      this.driveRenameMenuOpen = false
    },
    onMenuItemClick(item) {
      this.showMenu = false
      this.$emit('picker-toggle', false)
      item.action()
    },
    cancelDriveClose() {
      if (this.driveMenuCloseTimer) {
        clearTimeout(this.driveMenuCloseTimer)
        this.driveMenuCloseTimer = null
      }
    },
    openDriveMenu() {
      this.cancelDriveClose()
      this.driveMenuOpen = true
    },
    scheduleDriveClose() {
      this.cancelDriveClose()
      this.driveMenuCloseTimer = setTimeout(() => {
        this.driveMenuOpen = false
      }, 300)
    },
    onDriveMenuInput(isOpen) {
      if (!isOpen) this.driveMenuOpen = false
    },
    cancelDriveRenameClose() {
      if (this.driveRenameMenuCloseTimer) {
        clearTimeout(this.driveRenameMenuCloseTimer)
        this.driveRenameMenuCloseTimer = null
      }
    },
    openDriveRenameMenu() {
      this.cancelDriveRenameClose()
      this.driveRenameMenuOpen = true
    },
    scheduleDriveRenameClose() {
      this.cancelDriveRenameClose()
      this.driveRenameMenuCloseTimer = setTimeout(() => {
        this.driveRenameMenuOpen = false
      }, 300)
    },
    onDriveRenameMenuInput(isOpen) {
      if (!isOpen) this.driveRenameMenuOpen = false
    },
  },
}
</script>

<template>
  <div data-test="message-actions" class="actions-wrapper">
    <v-menu
      v-model="showMenu"
      offset-y
      left
      :nudge-bottom="4"
      content-class="chat-actions-menu"
      @input="onMenuToggle"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          icon
          x-small
          data-test="action-trigger"
          class="action-trigger"
          :style="{ backgroundColor: bubbleColor }"
          v-bind="attrs"
          v-on="on"
          @click.stop
        >
          <icon-svg name="mdi-chevron-down" size="16" color="#8E8E8E" />
        </v-btn>
      </template>

      <div @click.stop>
        <!-- Emoji reactions bar (above menu items) -->
        <div v-if="canReact" class="emoji-bar d-flex align-center px-2 py-1">
          <v-btn
            v-for="emoji in quickEmojis"
            :key="emoji"
            class="emoji-btn"
            icon
            small
            @click="onSelectEmoji(emoji)"
          >
            {{ emoji }}
          </v-btn>
        </div>

        <v-divider v-if="canReact && menuItems.length > 0" />

        <!-- Action items (Drive-style) -->
        <v-list class="az-list-no-icon-spacing py-1" dense>
          <v-list-item
            v-for="item in menuItems"
            :key="item.key"
            :data-test="'action-' + item.key"
            @click="onMenuItemClick(item)"
          >
            <v-list-item-icon>
              <icon-svg :name="item.icon" size="16" color="#8E8E8E" />
            </v-list-item-icon>
            <v-list-item-title>
              {{ item.label }}
            </v-list-item-title>
          </v-list-item>

          <!-- Enviar para AAWZ Drive — abre a árvore de pastas em cascata. -->
          <v-menu
            v-if="canSendToDrive"
            :value="driveMenuOpen"
            offset-x
            right
            nudge-right="4"
            content-class="chat-actions-menu"
            @input="onDriveMenuInput"
          >
            <template v-slot:activator="{ attrs }">
              <v-list-item
                v-bind="attrs"
                data-test="action-drive"
                @mouseenter="openDriveMenu"
                @mouseleave="scheduleDriveClose"
              >
                <v-list-item-icon>
                  <icon-svg :name="driveIcon" size="16" color="#8E8E8E" />
                </v-list-item-icon>
                <v-list-item-title>{{ sendToDriveLabel }}</v-list-item-title>
                <icon-svg name="mdi-chevron-right" size="16" color="#8E8E8E" />
              </v-list-item>
            </template>
            <div
              @click.stop
              @mouseenter="cancelDriveClose"
              @mouseleave="scheduleDriveClose"
            >
              <v-list class="az-list-no-icon-spacing py-1" dense>
                <v-list-item
                  data-test="action-drive-root"
                  :disabled="!driveTreeLoaded"
                  @click="onPickDriveFolder(driveRootNode)"
                >
                  <v-list-item-title>AAWZ Drive (raiz)</v-list-item-title>
                </v-list-item>
                <v-divider v-if="driveRootFolders.length" />
                <AawzDriveFolderPickerMenu
                  :folders="driveRootFolders"
                  @select="onPickDriveFolder"
                  @active="cancelDriveClose"
                />
              </v-list>
            </div>
          </v-menu>

          <!-- Enviar para AAWZ Drive e renomear — mesma árvore, o pai abre o
               modal de renomear antes do upload. -->
          <v-menu
            v-if="canSendToDrive"
            :value="driveRenameMenuOpen"
            offset-x
            right
            nudge-right="4"
            content-class="chat-actions-menu"
            @input="onDriveRenameMenuInput"
          >
            <template v-slot:activator="{ attrs }">
              <v-list-item
                v-bind="attrs"
                data-test="action-drive-rename"
                @mouseenter="openDriveRenameMenu"
                @mouseleave="scheduleDriveRenameClose"
              >
                <v-list-item-icon>
                  <icon-svg :name="driveIcon" size="16" color="#8E8E8E" />
                </v-list-item-icon>
                <v-list-item-title>
                  {{ sendToDriveRenameLabel }}
                </v-list-item-title>
                <icon-svg name="mdi-chevron-right" size="16" color="#8E8E8E" />
              </v-list-item>
            </template>
            <div
              @click.stop
              @mouseenter="cancelDriveRenameClose"
              @mouseleave="scheduleDriveRenameClose"
            >
              <v-list class="az-list-no-icon-spacing py-1" dense>
                <v-list-item
                  data-test="action-drive-rename-root"
                  :disabled="!driveTreeLoaded"
                  @click="onPickDriveFolderRename(driveRootNode)"
                >
                  <v-list-item-title>AAWZ Drive (raiz)</v-list-item-title>
                </v-list-item>
                <v-divider v-if="driveRootFolders.length" />
                <AawzDriveFolderPickerMenu
                  :folders="driveRootFolders"
                  @select="onPickDriveFolderRename"
                  @active="cancelDriveRenameClose"
                />
              </v-list>
            </div>
          </v-menu>

          <v-list-item
            v-if="canDelete"
            data-test="action-delete"
            @click="onMenuItemClick({ action: onDelete })"
          >
            <v-list-item-icon>
              <icon-svg :name="icons.delete" size="16" color="#8E8E8E" />
            </v-list-item-icon>
            <v-list-item-title>Remover nota</v-list-item-title>
          </v-list-item>
        </v-list>
      </div>
    </v-menu>
  </div>
</template>

<style lang="sass" scoped>
.actions-wrapper
  position: absolute
  top: 2px
  right: 4px
  z-index: 2

.emoji-bar
  gap: 2px
  justify-content: center

.emoji-btn
  font-size: 16px
  ::v-deep .v-btn__content
    font-size: 16px
    color: initial
    opacity: 1
  &:hover
    background-color: rgba(0, 0, 0, 0.06)
    transform: scale(1.3)

// Os dois v-menu de Drive (Enviar / Enviar e renomear) ficam como filhos
// diretos do v-list de ações. O wrapper de ativação do v-menu
// (<div class="v-menu v-menu--inline">) é 0x0 mas ainda participa do layout
// como irmão dos v-list-item, inflando o espaçamento. display:contents
// remove da árvore de layout; não afeta o conteúdo teletransportado do menu
// (content-class) nem o ativador (Vuetify mede o v-list-item, não este
// wrapper, pra posicionar o conteúdo).
::v-deep .az-list-no-icon-spacing > .v-menu.v-menu--inline
  display: contents
</style>

<style lang="sass">
.chat-actions-menu
  border-radius: 8px !important
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important
  overflow: hidden

  .v-list-item
    min-height: 36px
    cursor: pointer
</style>
